import { useEffect, useRef, useState } from 'react';
import { T, num } from '../../../../theme/tokens';
import { DateRange } from './DateRange';
import type { Range } from './DateRange';
import type { ProfitFilterSelection } from '../../../../services/api';

/* Avi's filters (14/10): dates, payer, service, therapist, clinic.
   Four of the five are live. The fifth, "אזור", is deliberately absent — measured
   18/10, `patient_status.area` is empty on 17,627 of ~21,000 rows, so a region
   filter would quietly discard five patients in six the moment anyone used it.

   🔴 Every option carries its meeting count. That is not decoration: it is what
   stops someone selecting a clinic with 37 meetings and reading the resulting
   margin as a finding about the business. */

export interface Option { value: string; label?: string; n: number }
export interface Options {
  payers: Option[]; clinics: Option[]; therapists: Option[]; services: Option[];
}

export type FilterKey = 'payers' | 'services' | 'therapists' | 'clinics';

export const FILTER_HE: Record<FilterKey, string> = {
  payers: 'קופה', services: 'סוג שירות', therapists: 'מטפל', clinics: 'מרפאה',
};

/* Payer names arrive as the clinical system stores them. Translating in the UI and
   not in the query is deliberate — the value sent back to the API has to be the one
   BigQuery holds, or the filter silently matches nothing. */
const PAYER_HE: Record<string, string> = {
  Maccabi: 'מכבי', 'Maccabi Youth': 'מכבי נוער', Clalit: 'כללית',
  ITC: 'ITC / חוסן', Private: 'פרטי', Donation: 'תרומה',
};

const labelOf = (key: FilterKey, o: Option) =>
  o.label ?? (key === 'payers' ? PAYER_HE[o.value] ?? o.value : o.value);

function Dropdown({ filterKey, options, selected, onChange }: {
  filterKey: FilterKey; options: Option[];
  selected: string[]; onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef<HTMLDivElement>(null);

  // Close on any click outside. Without this a second dropdown opens behind the
  // first and the two panels overlap.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  const visible = q
    ? options.filter((o) => labelOf(filterKey, o).toLowerCase().includes(q.toLowerCase()))
    : options;
  const on = selected.length > 0;

  return (
    <div ref={box} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 12, fontWeight: on ? 700 : 500,
          color: on ? T.blue : T.ink,
          background: on ? T.blueTint : T.card,
          border: `1px solid ${on ? T.blue : T.border}`,
          borderRadius: 7, padding: '6px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        {FILTER_HE[filterKey]}
        <span style={{ fontWeight: 400, color: on ? T.blue : T.inkFaint }}>
          {on ? (selected.length === 1 ? labelOf(filterKey, { value: selected[0], n: 0 }) : `${selected.length} נבחרו`) : 'הכל'}
        </span>
        <span aria-hidden style={{ fontSize: 9, color: T.inkFaint }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 5px)', insetInlineStart: 0,
          zIndex: 40, minWidth: 236, maxHeight: 320, overflowY: 'auto',
          background: T.card, border: `1px solid ${T.border}`,
          borderRadius: T.radiusSm, boxShadow: T.shadowRaised, padding: 6,
        }}>
          {/* 67 therapists is more than anyone scans. Search appears only where the
              list is long enough to need it. */}
          {options.length > 12 && (
            <input
              autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="חיפוש…"
              style={{
                width: '100%', fontSize: 12, padding: '6px 8px', marginBottom: 4,
                border: `1px solid ${T.border}`, borderRadius: 6, color: T.ink,
              }}
            />
          )}
          {on && (
            <button
              onClick={() => { onChange([]); setQ(''); }}
              style={{
                width: '100%', textAlign: 'start', fontSize: 11.5, color: T.inkSoft,
                background: 'none', border: 'none', padding: '5px 8px', cursor: 'pointer',
              }}
            >
              נקה בחירה
            </button>
          )}
          {visible.length === 0 && (
            <div style={{ fontSize: 11.5, color: T.inkFaint, padding: '8px' }}>אין התאמה</div>
          )}
          {visible.map((o) => {
            const checked = selected.includes(o.value);
            return (
              <label
                key={o.value}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 12.5, padding: '6px 8px', borderRadius: 6, cursor: 'pointer',
                  background: checked ? T.blueTint : 'transparent',
                  color: checked ? T.ink : T.inkSoft, fontWeight: checked ? 700 : 400,
                }}
              >
                <input type="checkbox" checked={checked} onChange={() => toggle(o.value)}
                       style={{ accentColor: T.blue, margin: 0 }} />
                <span style={{ flex: 1, minWidth: 0 }}>{labelOf(filterKey, o)}</span>
                {o.n > 0 && (
                  <span style={{ fontSize: 10.5, color: T.inkFaint, fontVariantNumeric: 'tabular-nums' }}>
                    {num(o.n)}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function Filters({ range, onRange, options, selection, onSelection, loading }: {
  range: Range; onRange: (r: Range) => void;
  options: Options | null;
  selection: ProfitFilterSelection;
  onSelection: (next: ProfitFilterSelection) => void;
  loading?: boolean;
}) {
  const keys: FilterKey[] = ['payers', 'services', 'therapists', 'clinics'];
  const anyOn = keys.some((k) => (selection[k] ?? []).length > 0);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <DateRange value={range} onChange={onRange} />

      <span aria-hidden style={{ width: 1, height: 20, background: T.border, margin: '0 2px' }} />

      {keys.map((k) => (
        <Dropdown
          key={k} filterKey={k}
          options={options ? options[k] : []}
          selected={selection[k] ?? []}
          onChange={(next) => onSelection({ ...selection, [k]: next })}
        />
      ))}

      {anyOn && (
        <button
          onClick={() => onSelection({})}
          style={{
            fontSize: 11.5, color: T.inkSoft, background: 'none',
            border: `1px solid ${T.border}`, borderRadius: 7,
            padding: '6px 10px', cursor: 'pointer',
          }}
        >
          נקה הכל
        </button>
      )}

      {loading && <span style={{ fontSize: 11.5, color: T.inkFaint }}>מסנן…</span>}
      <span style={{ fontSize: 11, color: T.inkFaint, marginInlineStart: 'auto' }}>
        הכל לפני מע״מ
      </span>
    </div>
  );
}

/* The marker a panel wears when the current filter could not reach it. The backend
   decides this (`filters_not_applied`), because the backend is what knows which
   query carries which column. */
export function NotFiltered({ names }: { names?: string[] }) {
  if (!names || names.length === 0) return null;
  return (
    <span
      title={`הלוח הזה אינו מצטמצם לפי ${names.join(' ו')} — הנתון שמאחוריו אינו נושא את החתך הזה. המספרים כאן הם של כלל הפעילות.`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 10, fontWeight: 700, color: '#92400E', background: T.orangeTint,
        border: '1px solid #FDE2B2', borderRadius: 5, padding: '1px 6px',
        cursor: 'help', whiteSpace: 'nowrap',
      }}
    >
      ללא סינון {names.join(' · ')}
    </span>
  );
}
